import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../legal/legal.css';
import { EventGrid, EventGridSkeleton, extractEvents, WebEvent } from './EventGrid';
import { readCachedEvents, writeCachedEvents } from './eventCache';

const API = 'https://maineventbackend.seuytel.com/api/';

function buildSearchUrl(params: URLSearchParams): string {
    const next = new URLSearchParams();
    next.set('size', params.get('size') || '24');
    next.set('page', params.get('page') || '1');

    const query = params.get('query') || params.get('q') || params.get('keywords') || '';
    if (query) {
        next.set('q', query);
        next.set('keywords', query);
    }

    const location = params.get('location') || params.get('city') || '';
    if (location) next.set('city', location);

    [...params.getAll('category[]'), ...params.getAll('category')].forEach((id) => {
        if (id) next.append('category[]', id);
    });

    ['date_from', 'date_to', 'min_price', 'max_price', 'radius', 'lat', 'lon'].forEach((key) => {
        const value = params.get(key);
        if (value) next.set(key, value);
    });

    const online = params.get('is_online');
    if (online === 'true' || online === '1') next.set('is_online', '1');
    if (online === 'false' || online === '0') next.set('is_online', '0');

    return `${API}search/events?${next.toString()}`;
}

export default function EventResultsPage() {
    const [params] = useSearchParams();
    const [events, setEvents] = useState<WebEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const searchUrl = useMemo(() => buildSearchUrl(params), [params]);
    const title = params.get('query') || params.get('q') || params.get('keywords') || params.get('location') || params.get('city') || '';

    useEffect(() => {
        let cancelled = false;
        const cacheKey = `me.events.search.v1.${searchUrl}`;
        const cached = readCachedEvents(cacheKey);
        if (cached) {
            setEvents(cached);
            setLoading(false);
        } else {
            setLoading(true);
        }
        setError('');

        fetch(searchUrl, { headers: { Accept: 'application/json' } })
            .then((res) => {
                if (!res.ok) throw new Error(`Events konnten nicht geladen werden (${res.status}).`);
                return res.json();
            })
            .then((payload) => {
                if (!cancelled) {
                    const nextEvents = extractEvents(payload);
                    setEvents(nextEvents);
                    writeCachedEvents(cacheKey, nextEvents);
                }
            })
            .catch((e) => {
                if (!cancelled && !cached) {
                    setEvents([]);
                    setError(e.message || 'Events konnten nicht geladen werden.');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [searchUrl]);

    return (
        <main className="se-content se-results-page">
            <section className="se-results-head">
                <span className="se-hc-eyebrow">Entdecken</span>
                <h1>{title ? `Events für "${title}"` : 'Events entdecken'}</h1>
                <p>Suche nach Events, Orten und Kategorien. Die Ergebnisse kommen aus derselben Event-Suche wie in der App.</p>
            </section>

            {loading ? (
                <EventGridSkeleton count={12} />
            ) : error ? (
                <p className="se-event-empty">{error}</p>
            ) : (
                <EventGrid events={events} emptyText="Keine passenden Events gefunden." />
            )}
        </main>
    );
}
